import { call, delay } from 'redux-saga/effects';
import FrontSonda from '@gcloud-npm/utils.monitoring.front-sonda';
import { getUUID } from '../../utils/monitoring';
import {
  generalAdabyronBehaviour,
  USER_TYPES,
} from '../../utils/adaByron';
import { MethodType, Context } from '../../utils/types';
import Request, { getMockedInfo } from '../../utils/request';

export enum PATHS {
  INIT_CARD_PAYMENT = 'venta/pago/{cqId}/tarjeta',
  INIT_FINANCED_PAYMENT = 'venta/pago/{cqId}/financiado',
  CHECK_FINANCED_PAYMENT_STATUS = 'venta/pago/{cqId}/financiado/status/{spoId}/{internalId}/',
  GET_CARD_PAYMENT_ERROR = '/static/json/buyProcess/cardPaymentErrors.json',
}

export const initCardPayment = (
  data: {
    cqId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.INIT_CARD_PAYMENT,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.OPEN],
    eventName: 'PAYMENT.INIT_CARD_PAYMENT',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'initCardPayment',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export const initFinancedPayment = (
  data: {
    cqId: string;
    mobileLine: string;
    months: number;
    rateCode: number;
    taxVersion: number;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const {
    cqId,
    mobileLine,
    months,
    rateCode,
    taxVersion,
  } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.INIT_FINANCED_PAYMENT,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PAYMENT.INIT_FINANCED_PAYMENT',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'initFinancedPayment',
    methodType: MethodType.POST,
    requestObject: {
      lineM: mobileLine,
      codTarifa: rateCode,
      versionTarifa: taxVersion,
      numPlazos: months,
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 11,
  });
};

export const checkFinancedPaymentStatus = (
  data: {
    cqId: string;
    spoId: string;
    internalId: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, spoId, internalId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CHECK_FINANCED_PAYMENT_STATUS,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'PAYMENT.CHECK_FINANCED_PAYMENT_STATUS',
    urlParamsToReplace: [
      {
        param: '{cqId}',
        value: cqId,
      },
      { param: '{spoId}', value: spoId },
      { param: '{internalId}', value: internalId },
    ],
    mockedInfo: 'checkFinancedPaymentStatus',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
    telcoHostVersion: 10,
  });
};

export function* checkFinancedPaymentStatusLoopGenerator(
  data: {
    cqId: string;
    spoId: string;
    internalId: string;
    maxTries: number;
  },
  context: Context,
  idFront = 'VT',
): unknown {
  let { maxTries } = data;
  let urlSupplier = null;
  const checkFinancedPaymentDataObj = {
    ...data,
  };
  delete checkFinancedPaymentDataObj.maxTries;
  while (maxTries > 0 && !urlSupplier) {
    const responsePromise = yield call(
      checkFinancedPaymentStatus,
      checkFinancedPaymentDataObj,
      context,
      idFront,
    );
    console.log('responsePromise', responsePromise);
    if (responsePromise && responsePromise.urlSupplier) {
      ({ urlSupplier } = responsePromise);
    } else {
      yield delay(1000);
    }
    maxTries -= 1;
  }
  if (urlSupplier) {
    return { urlSupplier };
  }
  return new Error('No se pudo recuperar urlSupplier');
}

export const getCardPaymentErrorError = (
  xhr: XMLHttpRequest,
  frontSonda: FrontSonda,
): Partial<XMLHttpRequest> => {
  frontSonda.errorMessage({
    info: `Ha fallado la petición al json: ${frontSonda.processName}`,
  });
  const objError = {
    status: xhr.status,
    statusText: xhr.statusText,
    response: null,
  };

  try {
    objError.response = JSON.parse(xhr.response);
  } catch (error) {
    objError.response = xhr.response;
  }

  return objError;
};

export const getCardPaymentErrorSuccess = (
  xhr: XMLHttpRequest,
  idError: string,
): string => {
  const { response } = xhr;
  const cardPaymentErrorsObj = JSON.parse(response);
  const { errors, messages } = cardPaymentErrorsObj;
  const errorType = errors[idError] || 'Default';
  return messages[errorType];
};

export const getCardPaymentError = (
  data: {
    idError: string;
  },
  context: Context,
  idFront = 'VT',
): Promise<unknown> => {
  const idRequest = getUUID(idFront);
  const { idError } = data;
  const frontSonda = new FrontSonda({
    processName: 'PAYMENT.GET_CARD_PAYMENT_ERROR',
    context,
    filePath: 'adabyron/payment',
  } as any);
  const { bucket: connectionData } = context.environment;
  const { endpoint: host, headers } = connectionData;
  const { name: envName } = context.environment;

  const URL_API = `${host}${PATHS.GET_CARD_PAYMENT_ERROR}?${idRequest}`;
  const mockInfo = getMockedInfo('getCardPaymentErrors', envName);

  try {
    const request = new Request({
      method: MethodType.GET,
      endpoint: URL_API,
      onError: (response: XMLHttpRequest) =>
        getCardPaymentErrorError(response, frontSonda),
      onSuccess: (response: XMLHttpRequest) =>
        getCardPaymentErrorSuccess(response, idError),
      headers: {
        ...headers,
        'Content-Type': 'application/json;charset=UTF-8',
      },
      mockInfo,
    });

    return request.makeRequest();
  } catch (err) {
    frontSonda.errorMessage({
      error: err,
      info: 'Ha fallado js durante la invocación al json',
    });
    return null;
  }
};
